<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
      <div class="text-muted small mr-3">
        {{ $tc('adminAccountManagement.list.totalRows', accounts.length, { count: accounts.length }) }}
      </div>
      <b-input-group class="search-group d-flex align-items-center">
        <b-icon icon="search" aria-hidden="true" scale="1"></b-icon>
        <b-form-input
          id="filter-input-admin-list"
          v-model="filter"
          type="search"
          :placeholder="$t('webmag.search')"
        ></b-form-input>
      </b-input-group>
    </div>
    <b-table
      :items="accounts"
      :fields="fields"
      :filter="filter"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :per-page="perPage"
      :current-page="currentPage"
      sort-icon-left
      hover
      borderless
      dark
      show-empty
      class="table-view admin-table"
      @row-clicked="onRowClicked"
      @filtered="onFiltered"
    >
      <template #empty>
        <div class="text-center my-2">
          <b-icon icon="exclamation-circle-fill" class="mr-2" aria-hidden="true"></b-icon>
          {{ $t('adminAccountManagement.noAccounts') }}
        </div>
      </template>
      <template #emptyfiltered>
        <div class="text-center my-2">
          <b-icon icon="exclamation-circle-fill" class="mr-2" aria-hidden="true"></b-icon>
          {{ $t('adminAccountManagement.list.noFilteredAccounts') }}
        </div>
      </template>

      <template #cell(name)="data">
        <div class="account-cell">
          <div class="account-name">{{ data.item.name }}</div>
          <div
            v-if="data.item.parent_name || data.item.slug"
            class="account-meta"
          >
            <span v-if="data.item.parent_name">{{ data.item.parent_name }}</span>
            <span
              v-if="data.item.parent_name && data.item.slug"
              class="mx-1"
            >·</span>
            <span v-if="data.item.slug">/{{ data.item.slug }}</span>
          </div>
        </div>
      </template>

      <template #cell(status)="data">
        <span class="flag-row">
          <span
            v-if="isDisabled(data.item)"
            class="status-flag flag-danger"
            v-b-tooltip.hover
            :title="$t('adminAccountManagement.flag.disabled')"
          >
            <b-icon icon="slash-circle" aria-hidden="true" scale=".85"></b-icon>
          </span>
          <span
            v-if="isTestAccount(data.item)"
            :class="['status-flag', isTrialExpired(data.item) ? 'flag-danger' : 'flag-warn']"
            v-b-tooltip.hover
            :title="trialTooltip(data.item)"
          >
            <b-icon icon="hourglass-split" aria-hidden="true" scale=".85"></b-icon>
            <span v-if="trialDays(data.item) !== null" class="ml-1">
              {{ trialDays(data.item) }}d
            </span>
          </span>
          <span
            v-if="isVue3Editor(data.item)"
            class="status-flag flag-info"
            v-b-tooltip.hover
            :title="$t('adminAccountManagement.flag.vue3Editor')"
          >
            V3
          </span>
        </span>
      </template>

      <template #cell(page_count)="data">
        <span class="numeric-cell">{{ data.item.page_count }}</span>
      </template>

      <template #cell(project_count)="data">
        <span class="numeric-cell">{{ data.item.project_count }}</span>
      </template>

      <template #cell(last_activity_ms)="data">
        <span
          v-if="data.item.last_activity_ms"
          v-b-tooltip.hover
          :title="formatDateTime(data.item.last_activity_ms)"
        >
          {{ formatRelative(data.item.last_activity_ms) }}
        </span>
        <span v-else class="text-muted">
          {{ $t('adminAccountManagement.stats.never') }}
        </span>
      </template>
    </b-table>

    <div
      v-if="filteredCount > perPage"
      class="d-flex justify-content-center mt-2"
    >
      <b-pagination
        v-model="currentPage"
        :total-rows="filteredCount"
        :per-page="perPage"
        align="center"
        size="sm"
      ></b-pagination>
    </div>
  </div>
</template>

<script>
import { formatDateTime, formatRelative } from './dateFormatters';

const TRIAL_DAYS = 14;
const DAY = 24 * 60 * 60 * 1000;

export default {
  name: 'AdminAccountTable',
  props: {
    accounts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      filter: '',
      sortBy: 'name',
      sortDesc: false,
      perPage: 50,
      currentPage: 1,
      filteredCount: 0,
    };
  },
  computed: {
    fields() {
      return [
        {
          key: 'name',
          label: this.$t('adminAccountManagement.list.name'),
          sortable: true,
          tdClass: 'name-col',
        },
        {
          key: 'status',
          label: this.$t('adminAccountManagement.list.status'),
          sortable: false,
          tdClass: 'status-col',
        },
        {
          key: 'page_count',
          label: this.$t('adminAccountManagement.list.pages'),
          sortable: true,
          class: 'text-right',
          tdClass: 'numeric-col',
        },
        {
          key: 'project_count',
          label: this.$t('adminAccountManagement.list.projects'),
          sortable: true,
          class: 'text-right',
          tdClass: 'numeric-col',
        },
        {
          key: 'last_activity_ms',
          label: this.$t('adminAccountManagement.list.lastActivity'),
          sortable: true,
          tdClass: 'activity-col',
        },
      ];
    },
  },
  watch: {
    accounts() {
      this.filteredCount = this.accounts.length;
      this.currentPage = 1;
    },
    filter() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.filteredCount = this.accounts.length;
  },
  methods: {
    formatDateTime(value) {
      return formatDateTime(value, this.$root?.$i18n?.locale || 'en-US');
    },
    formatRelative(value) {
      return formatRelative(value, this.$root?.$i18n?.locale || 'en-US');
    },
    isTestAccount(item) {
      return item?.settings?.isTestAccount === true;
    },
    isDisabled(item) {
      return item?.settings?.disableAccount === true;
    },
    isVue3Editor(item) {
      return item?.settings?.enable_vue3_editor === true;
    },
    trialDays(item) {
      if (!this.isTestAccount(item) || !item.created_at) return null;
      const expires = new Date(item.created_at).getTime() + TRIAL_DAYS * DAY;
      return Math.ceil((expires - Date.now()) / DAY);
    },
    isTrialExpired(item) {
      const days = this.trialDays(item);
      return days !== null && days <= 0;
    },
    trialTooltip(item) {
      const days = this.trialDays(item);
      if (days === null) return this.$t('adminAccountManagement.flag.testAccount');
      if (days <= 0) return this.$t('adminAccountManagement.flag.trialExpired');
      return this.$t('adminAccountManagement.flag.trialDaysRemaining', { days });
    },
    onRowClicked(item) {
      this.$emit('account-selected', item);
    },
    onFiltered(filteredItems) {
      this.filteredCount = filteredItems.length;
    },
  },
};
</script>

<style scoped lang="scss">
.search-group {
  width: 250px;
  background-color: #5b6064;
  padding-left: 11px;
  border-radius: 3px;
}

#filter-input-admin-list,
#filter-input-admin-list:focus {
  color: #b9b9b9;
  background-color: transparent;
  border: none;
  outline: none;
  box-shadow: none;
  height: calc(1.5em + 0.75rem);
}

#filter-input-admin-list::-webkit-search-cancel-button {
  appearance: none;
  height: 23px;
  width: 20px;
  background-size: 21px;
  background: url('../../../assets/svg/icon-search-cancel.svg') no-repeat center center;
  cursor: pointer;
}

.account-cell {
  line-height: 1.25;
}

.account-name {
  color: #fff;
  font-weight: 600;
}

.account-meta {
  color: #9a9a9a;
  font-size: 11px;
  margin-top: 2px;
}

.numeric-cell {
  display: inline-block;
  text-align: right;
  min-width: 2ch;
}

.flag-row {
  white-space: nowrap;
}

.status-flag {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 3px;
  margin-right: 3px;
}

.flag-warn {
  background-color: rgba(255, 193, 7, 0.18);
  color: #ffc107;
}

.flag-danger {
  background-color: rgba(220, 83, 83, 0.22);
  color: #ff8a8a;
}

.flag-info {
  background-color: rgba(79, 70, 229, 0.22);
  color: #a3a3ff;
}

::v-deep table.admin-table {
  font-size: 14px;
  border-collapse: separate;
  border-spacing: 0 6px;
  color: $webmag-light-grey;

  thead th {
    text-transform: uppercase;
    font-size: 12px;
    padding: 8px 10px;
    border-bottom: none;
  }

  tbody tr {
    background-color: #53575B;
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
    cursor: pointer;
    transition: background-color 0.12s ease-in-out;

    &:hover {
      background-color: #5e6266;
    }

    > td {
      padding: 10px;
      vertical-align: middle;
    }

    td.numeric-col {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }

    td.activity-col {
      white-space: nowrap;
    }
  }
}

::v-deep .table-dark {
  background-color: transparent;
}
</style>
